import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EmpowermentFinancingService } from '../../../../Component/Api/EmpowermentFinancing/EmpowermentFinancingService';
import {
	EFTargetPeriod,
	IEFConfiguration,
	IEFTargets,
	IEFTargetSettingsDtoIn,
} from '../../../../Component/Model/Dto/EmpowermentFinancing';
import { PageTitle } from '../../../../Component/Common/Components/PageTitle';
import colors from '../../../../assets/theme/base/colors';
import { buildTargetPreview } from '../../../../Helpers/EmpowermentFinancing/targetPreviewHelper';
import { groupRandInput, parseRandInput } from '../../../../Helpers/EmpowermentFinancing/formatHelper';
import { ProgrammeEnvelopePanel } from './ProgrammeEnvelopePanel';
import { RulesInEffectPanel } from './RulesInEffectPanel';
import { SubRatiosPanel } from './SubRatiosPanel';
import { TargetsTable } from './TargetsTable';
import { SubHeading } from './TargetSettingsLabels';

interface IRouteParams {
	BBBEEScorecardId: string;
}

const equalAllocationRatio = 25;

export const TargetSettingsPage = () => {
	const { BBBEEScorecardId } = useParams<IRouteParams>();

	const [configuration, setConfiguration] = useState<IEFConfiguration | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [hasSaved, setHasSaved] = useState(false);

	const [targetPeriodId, setTargetPeriodId] = useState<EFTargetPeriod>(EFTargetPeriod.TwoThousandTwentyFourToTwentyEight);
	const [applicableYear, setApplicableYear] = useState<number>(0);
	const [financialYearEnd, setFinancialYearEnd] = useState('');
	const [monetaryTargetRaw, setMonetaryTargetRaw] = useState('');
	const [ratios, setRatios] = useState<Record<string, number>>({});
	const [savedTargets, setSavedTargets] = useState<IEFTargets | null>(null);

	const monetaryTarget = parseRandInput(monetaryTargetRaw);

	const selectedPeriod = useMemo(
		() => configuration?.targetPeriods.find(p => p.id === targetPeriodId) ?? null,
		[configuration, targetPeriodId]
	);

	const periodYears = useMemo(() => {
		if (!configuration || !selectedPeriod) {
			return [];
		}
		return Array.from({ length: configuration.periodYearCount }, (_, index) => selectedPeriod.startYear + index);
	}, [configuration, selectedPeriod]);

	const ratioTotal = useMemo(() => Object.values(ratios).reduce((sum, value) => sum + value, 0), [ratios]);
	const isRatioTotalValid = Math.round(ratioTotal * 100) / 100 === 100;
	const isFinancialYearEndCaptured = financialYearEnd !== '';
	const canSave = isRatioTotalValid && isFinancialYearEndCaptured && monetaryTarget > 0;

	const applyDefaults = useCallback((config: IEFConfiguration) => {
		const defaults: Record<string, number> = {};
		config.subCategories.forEach(subCategory => {
			defaults[subCategory.key] = subCategory.defaultRatio;
		});
		setRatios(defaults);
	}, []);

	useEffect(() => {
		const load = async () => {
			setIsLoading(true);

			try {
				const configResponse = await EmpowermentFinancingService.getConfiguration();
				const config = configResponse.data;
				setConfiguration(config);

				const settingsResponse = await EmpowermentFinancingService.getSettings(BBBEEScorecardId);
				const saved = settingsResponse.data;

				if (saved) {
					setTargetPeriodId(saved.targetPeriodId);
					setApplicableYear(saved.applicableYear);
					setFinancialYearEnd(saved.financialYearEnd?.substring(0, 10) ?? '');
					setMonetaryTargetRaw(groupRandInput(saved.monetaryTargetFiveYear.toString()));
					setRatios({
						transformationalInfrastructure: saved.ratioTransformationalInfrastructure,
						blackAgriculturalFinancing: saved.ratioBlackAgriculturalFinancing,
						affordableHousing: saved.ratioAffordableHousing,
						blackBusinessGrowthFunding: saved.ratioBlackBusinessGrowthFunding,
					});
					setSavedTargets(saved.targets);
				} else {
					applyDefaults(config);
					const firstPeriod = config.targetPeriods[0];
					if (firstPeriod) {
						setTargetPeriodId(firstPeriod.id);
						setApplicableYear(firstPeriod.startYear);
					}
				}
			} catch {
				setLoadFailed(true);
			} finally {
				setIsLoading(false);
			}
		};

		load();
	}, [BBBEEScorecardId, applyDefaults]);

	const handleTargetPeriodChange = (value: EFTargetPeriod) => {
		setTargetPeriodId(value);
		const period = configuration?.targetPeriods.find(p => p.id === value);
		if (period) {
			setApplicableYear(period.startYear);
		}
		setSavedTargets(null);
	};

	const handleRatioChange = (key: string, value: number) => {
		setRatios(current => ({ ...current, [key]: value }));
		setSavedTargets(null);
	};

	const handleEqualAllocation = () => {
		if (!configuration) {
			return;
		}
		const allocated: Record<string, number> = {};
		configuration.subCategories.forEach(subCategory => {
			allocated[subCategory.key] = equalAllocationRatio;
		});
		setRatios(allocated);
		setSavedTargets(null);
	};

	const previewTargets = useMemo(() => {
		if (!configuration || !selectedPeriod) {
			return null;
		}
		return buildTargetPreview(configuration, {
			monetaryTargetFiveYear: monetaryTarget,
			startYear: selectedPeriod.startYear,
			ratioTransformationalInfrastructure: ratios.transformationalInfrastructure ?? 0,
			ratioBlackAgriculturalFinancing: ratios.blackAgriculturalFinancing ?? 0,
			ratioAffordableHousing: ratios.affordableHousing ?? 0,
			ratioBlackBusinessGrowthFunding: ratios.blackBusinessGrowthFunding ?? 0,
		});
	}, [configuration, selectedPeriod, monetaryTarget, ratios]);

	const displayedTargets = savedTargets ?? previewTargets;

	const handleSave = async () => {
		if (!canSave) {
			return;
		}

		setIsSaving(true);

		const payload: IEFTargetSettingsDtoIn = {
			scorecardId: BBBEEScorecardId,
			targetPeriodId,
			applicableYear,
			financialYearEnd,
			monetaryTargetFiveYear: monetaryTarget,
			ratioTransformationalInfrastructure: ratios.transformationalInfrastructure ?? 0,
			ratioBlackAgriculturalFinancing: ratios.blackAgriculturalFinancing ?? 0,
			ratioAffordableHousing: ratios.affordableHousing ?? 0,
			ratioBlackBusinessGrowthFunding: ratios.blackBusinessGrowthFunding ?? 0,
		};

		try {
			const response = await EmpowermentFinancingService.saveSettings(payload);
			setSavedTargets(response.data.targets);
			setHasSaved(true);
			setTimeout(() => setHasSaved(false), 3000);
		} catch {
			setHasSaved(false);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (loadFailed || !configuration || !selectedPeriod) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error">
					Target settings could not be loaded. Refresh the page to try again.
				</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: '100%' }}>
			<Box sx={{ mb: 3 }}>
				<PageTitle title="Target Settings - Empowerment Financing" />
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					Configure the BASA-confirmed 5-year monetary target and sub-ratios. All annual targets are derived
					automatically.
				</Typography>
			</Box>

			<Card sx={{ mb: 4 }}>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderBottom: '1px solid',
						borderColor: 'grey.200',
						bgcolor: colors.empowermentFinancing.sectionHeaderBackground,
					}}
				>
					<SubHeading sx={{ color: 'text.primary' }}>Configuration</SubHeading>
				</Box>
				<CardContent sx={{ p: 3 }}>
					<Grid container spacing={4} alignItems="stretch">
						<Grid item xs={12} md={7}>
							<ProgrammeEnvelopePanel
								targetPeriods={configuration.targetPeriods}
								targetPeriodId={targetPeriodId}
								onTargetPeriodChange={handleTargetPeriodChange}
								periodYears={periodYears}
								applicableYear={applicableYear}
								onApplicableYearChange={setApplicableYear}
								financialYearEnd={financialYearEnd}
								onFinancialYearEndChange={setFinancialYearEnd}
								monetaryTargetRaw={monetaryTargetRaw}
								monetaryTarget={monetaryTarget}
								onMonetaryTargetChange={value => {
									setMonetaryTargetRaw(value);
									setSavedTargets(null);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={5}>
							<SubRatiosPanel
								subCategories={configuration.subCategories}
								ratios={ratios}
								onRatioChange={handleRatioChange}
								onEqualAllocation={handleEqualAllocation}
								ratioTotal={ratioTotal}
								isRatioTotalValid={isRatioTotalValid}
							/>
						</Grid>
					</Grid>

					<RulesInEffectPanel qualifyingDate={selectedPeriod.qualificationThreshold} />
				</CardContent>
			</Card>

			{monetaryTarget > 0 && displayedTargets ? (
				<>
					<TargetsTable
						configuration={configuration}
						targets={displayedTargets}
						ratios={ratios}
						applicableYear={applicableYear}
					/>
					<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
						Period: {selectedPeriod.name}. Highlighted column ({applicableYear}) is the current applicable
						measurement year. Targets are cumulative - each year adds one equal annual instalment.
					</Typography>
				</>
			) : (
				<Card sx={{ mb: 4 }}>
					<CardContent sx={{ py: 6, textAlign: 'center' }}>
						<Typography variant="body2" sx={{ color: 'text.disabled' }}>
							Enter the 5-year monetary target above to see calculated targets.
						</Typography>
					</CardContent>
				</Card>
			)}

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving}>
					Save Settings
				</Button>
				{hasSaved && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: colors.empowermentFinancing.savedConfirmation }}>
						<CheckCircleIcon sx={{ fontSize: 18 }} />
						<Typography variant="body2" sx={{ fontWeight: 600, color: colors.empowermentFinancing.savedConfirmation }}>
							Settings saved - Scorecard targets updated
						</Typography>
					</Box>
				)}
				{!isRatioTotalValid && (
					<Alert severity="error" sx={{ py: 0, px: 1.5, fontSize: '0.8125rem' }}>
						Sub-ratios must sum to 100% before saving
					</Alert>
				)}
				{!isFinancialYearEndCaptured && (
					<Alert severity="error" sx={{ py: 0, px: 1.5, fontSize: '0.8125rem' }}>
						Capture the applicable financial year-end before saving
					</Alert>
				)}
			</Box>
		</Box>
	);
};
